import {ReactNode, useMemo} from 'react';

import {RootLayer} from '@sail/ui';

import {
  AppConfig,
  AppProfile,
  AppProvider,
  InlineDataProvider,
  useApp,
} from '@stripe-internal/conversations-assistant';
import {
  FeatureFlagsProvider as ConvUIFlagsProvider,
  type FeatureFlags as ConvUIIFlags,
  ThemeProvider as ConvUIThemeProvider,
  FileUploaderProvider,
  type FileUploaderValue,
  FileUploadValidationError,
} from '@stripe-internal/conversations-ui';
import {DefaultInlineDataComponentRegistry} from '@stripe-internal/mse-inline-data';

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;
const MAX_FILE_NAME_LENGTH = 200;

const supportedMimeTypes: string[] = [
  'image/jpeg',
  'image/png',
  'application/pdf',
  'text/plain',
  'text/xml',
  'text/csv',
  'text/tab-separated-values',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const mobileConvUIFlags: ConvUIIFlags = {
  newUniversalHelpDesign: true,
  universalHelpDotComDesign: false,
  dotComFeedbackButtons: false,
  dotComNoLlm: false,
  dotComCsf: false,
  messagesTopAnchored: false,
  inlineRacEmailForm: true,
  universalHelpDrawerEnabled: false,
  conversationsLiveAnswersRow: true,
  universalHelpEncourageMoreDetailFromUserMse5042: false,
};

type Props = {
  config: AppConfig;
  profile: AppProfile;
  children: ReactNode;
};

const validateFile = (file: File) => {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new FileUploadValidationError({
      message: 'File is too large',
      file,
      code: 'file_too_large',
    });
  }

  if (!supportedMimeTypes.includes(file.type)) {
    throw new FileUploadValidationError({
      message: 'File type is not supported',
      file,
      code: 'upload_type_not_supported',
    });
  }

  if (file.name.length > MAX_FILE_NAME_LENGTH) {
    throw new FileUploadValidationError({
      message: 'File name is too long',
      file,
      code: 'file_name_too_long',
    });
  }
};

const uploadFile = async ({
  fetch,
  file,
  abortSignal,
}: {
  fetch: typeof window.fetch;
  file: File;
  abortSignal?: AbortSignal;
}): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('purpose', 'support_attachment');

  const response = await fetch('/v1/files', {
    method: 'POST',
    body: formData,
    signal: abortSignal,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorCode = errorData?.error?.code;
    const errorMessage = errorData?.error?.message || 'File upload failed';

    if (errorCode === 'file_too_large' || errorMessage.includes('too large')) {
      throw new FileUploadValidationError({
        message: 'File is too large',
        file,
        code: 'file_too_large',
      });
    }

    if (
      errorCode === 'file_type_not_supported' ||
      errorMessage.includes('type')
    ) {
      throw new FileUploadValidationError({
        message: 'File type not supported',
        file,
        code: 'upload_type_not_supported',
      });
    }

    throw new Error(errorMessage);
  }

  const data = await response.json();
  return data.id;
};

function ConversationsUIFileUploadProvider({children}: {children: ReactNode}) {
  const app = useApp();

  const fileUploaderValue: FileUploaderValue = useMemo(
    () => ({
      available: true,
      upload: async (
        file: File,
        {abortSignal}: {abortSignal?: AbortSignal},
      ): Promise<string> => {
        try {
          return await uploadFile({
            fetch: app.api.fetch,
            file,
            abortSignal,
          });
        } catch (error: unknown) {
          if (error instanceof FileUploadValidationError) {
            throw error;
          }
          if (
            error &&
            typeof error === 'object' &&
            'name' in error &&
            error.name === 'AbortError'
          ) {
            throw error;
          }
          const errorMessage =
            error &&
            typeof error === 'object' &&
            'message' in error &&
            typeof error.message === 'string'
              ? error.message
              : 'Unknown error';
          throw new Error(`File upload failed: ${errorMessage}`);
        }
      },
      validateFile,
    }),
    [app.api.fetch],
  );

  return (
    <FileUploaderProvider value={fileUploaderValue}>
      {children}
    </FileUploaderProvider>
  );
}

export function MobileAssistantProviders({config, profile, children}: Props) {
  return (
    <ConvUIThemeProvider>
      <RootLayer>
        <AppProvider config={config} profile={profile}>
          <InlineDataProvider components={DefaultInlineDataComponentRegistry}>
            <ConvUIFlagsProvider value={mobileConvUIFlags}>
              <ConversationsUIFileUploadProvider>
                {children}
              </ConversationsUIFileUploadProvider>
            </ConvUIFlagsProvider>
          </InlineDataProvider>
        </AppProvider>
      </RootLayer>
    </ConvUIThemeProvider>
  );
}
